import { App, Drawer, type DrawerProps } from 'antd'
import { useCallback, useMemo, useRef, type CSSProperties } from 'react'

import { cx } from '@/Ship/Helpers/cssClass'

import { DrawerGuardContext, type DirtyGuard, type DrawerGuardApi } from './DrawerGuardContext'

import css from './AppDrawer.module.css'

interface AppDrawerProps extends DrawerProps {
    /** Core Drawer передає свій DrawerStore у шаблон як `context`; до antd він не потрапляє */
    context?: unknown
}

/** Ширина, яку ядро (ResourceDetail) підставляє, коли ресурс не задав свою */
const CORE_DEFAULT_WIDTH = 500

/** Наша ширина картки за замовчуванням; ресурси зі своєю шириною (заявка, задача…) мають пріоритет */
export const DEFAULT_DETAIL_WIDTH = 560

/**
 * Drawer застосунку (шаблон для Core Drawer через DrawerConfig): оформлення за токенами
 * теми і підтвердження закриття, якщо вміст зареєстрував guard незбережених змін
 * (хрестик, клік по масці та Escape проходять через один onClose).
 */
export const AppDrawer = ({
    onClose,
    className,
    rootClassName,
    styles: drawerStyles,
    children,
    context: _context,
    ...props
}: AppDrawerProps) => {
    const { modal } = App.useApp()
    const guardRef = useRef<DirtyGuard | null>(null)
    const confirmOpenRef = useRef(false)

    // Ядро задає ширину inline через styles.wrapper (rc-drawer її застосовує, хоча тип antd 6
    // цей ключ уже не описує) і не читає конфіг drawer, тож свій дефолт підставляємо тут:
    // лише замість дефолту ядра, явна ширина ресурсу лишається
    const semanticStyles = (drawerStyles ?? {}) as Record<string, CSSProperties | undefined>
    const wrapperWidth = semanticStyles['wrapper']?.width
    const styles = (
        wrapperWidth === CORE_DEFAULT_WIDTH
            ? { ...semanticStyles, wrapper: { ...semanticStyles['wrapper'], width: DEFAULT_DETAIL_WIDTH } }
            : drawerStyles
    ) as DrawerProps['styles']

    const guardApi = useMemo<DrawerGuardApi>(
        () => ({
            setGuard: (guard) => {
                guardRef.current = guard
            },
        }),
        []
    )

    const handleClose = useCallback<NonNullable<DrawerProps['onClose']>>(
        (event) => {
            if (guardRef.current?.()) {
                // Кілька швидких кліків по масці не мають відкривати кілька підтверджень
                if (confirmOpenRef.current) return
                confirmOpenRef.current = true
                modal.confirm({
                    title: 'Є незбережені зміни',
                    content: 'Закрити картку без збереження?',
                    okText: 'Закрити',
                    okButtonProps: { danger: true },
                    cancelText: 'Залишитись',
                    onOk: () => onClose?.(event),
                    afterClose: () => {
                        confirmOpenRef.current = false
                    },
                })
                return
            }
            onClose?.(event)
        },
        [modal, onClose]
    )

    return (
        <Drawer
            {...props}
            onClose={handleClose}
            rootClassName={`${cx(css, 'root')} ${rootClassName ?? ''}`}
            className={`${cx(css, 'drawer')} ${className ?? ''}`}
            {...(styles ? { styles } : {})}
        >
            <DrawerGuardContext.Provider value={guardApi}>{children}</DrawerGuardContext.Provider>
        </Drawer>
    )
}
